import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import usePromptWindowUnload from 'components/utils/prompt_window_unload';
import {
  EditFormMessageFromIframe,
  EditFormMessageFromIframeType,
  EditFormMessageToIframe,
  isEditFormMessageFromIframe
} from './types';

interface Props {
  editQuestionIframeSource: string;
  formId: string;
  setCanPublishForm?: (canPublish: boolean) => void;
  onVersionUpdate?: (version: number) => void;
  onSwitchToV1?: () => void;
}

interface Message {
  type: string;
  payload?: unknown;
}

export interface EditQuestionsIframeHandle {
  sendMessage: (message: EditFormMessageToIframe) => void;
}

const EditQuestionsIframe = forwardRef<EditQuestionsIframeHandle, Props>(
  (props: Props, ref): JSX.Element => {
    const { editQuestionIframeSource, formId, setCanPublishForm, onVersionUpdate, onSwitchToV1 } = props;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [lastSaveMessage, setLastSaveMessage] = useState<EditFormMessageFromIframe | null>(null);

    usePromptWindowUnload(
      lastSaveMessage?.type === EditFormMessageFromIframeType.PENDING_SAVE
      || lastSaveMessage?.type === EditFormMessageFromIframeType.SAVE_ERROR
    );

    const sendMessageToIframe = (message: Message | EditFormMessageToIframe) => {
      if (iframeRef.current?.contentWindow) {
        const targetOrigin = new URL(editQuestionIframeSource).origin;
        iframeRef.current.contentWindow.postMessage(message, targetOrigin);
      }
    };

    useImperativeHandle(ref, () => ({
      sendMessage: (message: EditFormMessageToIframe) => {
        sendMessageToIframe(message);
      }
    }));

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        const iframeOrigin = new URL(editQuestionIframeSource).origin;

        if (event.origin !== iframeOrigin) {
          return;
        }

        if (!isEditFormMessageFromIframe(event.data)) {
          return;
        }

        const message: EditFormMessageFromIframe = event.data;

        switch (message.type) {
          case EditFormMessageFromIframeType.PENDING_SAVE:
            setLastSaveMessage(message);
            setCanPublishForm?.(false);
            break;
          case EditFormMessageFromIframeType.CHANGES_SAVED:
            setLastSaveMessage(message);
            setCanPublishForm?.(true);
            if (message.version !== undefined) {
              onVersionUpdate?.(message.version);
            }
            break;
          case EditFormMessageFromIframeType.SAVE_ERROR:
            setLastSaveMessage(message);
            setCanPublishForm?.(false);
            break;
          case EditFormMessageFromIframeType.RELOAD:
            setLastSaveMessage(message);
            break;
          case EditFormMessageFromIframeType.SWITCH_TO_V1:
            onSwitchToV1?.();
            break;
          default:
            break;
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }, [editQuestionIframeSource, setCanPublishForm, setLastSaveMessage, onVersionUpdate, onSwitchToV1]);

    useEffect(() => {
      if (lastSaveMessage?.type === EditFormMessageFromIframeType.RELOAD) {
        const { path } = lastSaveMessage;
        if (path) {
          window.location.href = path;
        } else {
          window.location.reload();
        }
      }
    }, [lastSaveMessage]);

    const v2EditUrl = `${editQuestionIframeSource}/${formId}`;

    return (
      <iframe
        title={`edit-form-${formId}`}
        ref={iframeRef}
        className="iframe"
        src={v2EditUrl}
        onLoad={() => {
          sendMessageToIframe({ type: 'parent-ready' });
        }}
      />
    );
  }
);

export default EditQuestionsIframe;
